import api from '@api';
import { OrderStatusesTo } from 'skladon-types';
import { ORDER_GRID_ALL_PROVIDERS_URL, ORDER_GRID_URL } from 'src/resources/ordersGrid/sagas';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import { OrderType } from 'src/types/order';
import { GridRequestType, SelectRowType, TableRowType } from 'src/types/table';
import { checkPermission } from 'src/utils/checkPermission';
import dowloadFileFromData from 'src/utils/dowloadFileFromData';
import { getOrderProductDisplaySku } from 'src/utils/orderProductDisplay';
import { TABS } from './constants';
import { ClaimType } from 'src/types/claim';

export interface OrderProductsType {
    PRODUCT: {
        SKU: string;
    };
    SKU?: string;
    PRODUCT_SKU?: string;
    PRODUCT_PRICE: number;
    COUNT: number;
    CONFIRMED: number;
    MISSING_COUNT: number;
}

export interface OrderProductsResponseType {
    ID: number;
    ORDER_PRODUCTS: OrderProductsType[];
}

export interface OrdersProductsResponseType {
    data: {
        data: OrderProductsResponseType[];
    };
}

export const canReorderShipping = (order: OrderType): boolean => {
    if (!order) {
        return false;
    }
    return checkPermission(
        `ORDER.ACTION.SHIPPING.STATUS.${order.ORDER_TYPE?.toUpperCase()}.${order.STATUS?.toUpperCase()}`
    );
};

export const canEditAllowExpedition = (order: OrderType, orderStatusCodesObject: ConstantsCodesObjectType): boolean => {
    if (checkPermission('ORDER.EDITALL')) {
        return true;
    }
    if (checkPermission('ORDER.MANAGER_EDIT')) {
        return true;
    }
    if (!checkPermission('ORDER.UPDATE') || !order) {
        return false;
    }
    if (
        order.STATUS &&
        (orderStatusCodesObject[order.STATUS]?.ORDER_INT || 0) > (orderStatusCodesObject.packed?.ORDER_INT || 0)
    ) {
        return false;
    }
    return true;
};

export const canEditPrintFiles = (order: OrderType, orderStatusCodesObject: ConstantsCodesObjectType): boolean => {
    if (checkPermission('ORDER.EDITALL')) {
        return false;
    }
    if (checkPermission('ORDER.MANAGER_EDIT')) {
        return false;
    }
    if (!checkPermission('ORDER.UPDATE') || !order) {
        return true;
    }
    if (
        order.STATUS &&
        (orderStatusCodesObject[order.STATUS].ORDER_INT || 0) > (orderStatusCodesObject.received.ORDER_INT || 0)
    ) {
        return true;
    }
    return false;
};

export const getAllowedStatuses = (
    orderData: { STATUS: string },
    orderStatusCodes: ConstantsCodeType[],
    orderStatusCodesObject: ConstantsCodesObjectType
): ConstantsCodeType[] => {
    if (checkPermission('ORDER.EDITALL')) {
        return orderStatusCodes;
    }

    if (!orderData) {
        return [];
    }

    if (!checkPermission(`ORDER.STATUS.EDIT.${orderData.STATUS.toUpperCase()}`) && !checkPermission(`ORDER.PROPERTIES.STATUS.EDIT.${orderData.STATUS.toUpperCase()}`)) {
        return [];
    }

    return [
        ...(OrderStatusesTo[orderData.STATUS] || [])
            .filter((statusKey: string) => {
                return checkPermission(`ORDER.STATUS.LIST.${statusKey.toUpperCase()}`);
            })
            .map((statusKey: string) => {
                return orderStatusCodesObject[statusKey];
            })
    ];
};

export const dowlonadCn22Zip = async (selected: SelectRowType[]) => {
    return new Promise(async (resolve: any) => {
        const response = await api('post', `api/secure/orders/files/multiple`, {
            ids: selected.join(','),
            type: 'customDeclaration',
            name: 'CN22.pdf'
        });
        resolve();
        dowloadFileFromData(`data:application/x-zip-compressed;base64,`, response.data, 'CN22.zip');
    });
};

export const getExportRows = async (
    getAllGridParams: () => GridRequestType,
    getFormatedData: (data: OrderType[]) => OrderType[],
    selectedRows: SelectRowType[],
    allProviders: boolean,
    extendParams: { page: number; items: number },
    withExtendColmns?: boolean
): Promise<TableRowType[]> => {
    const params = getAllGridParams();
    const apiParams = {
        ...params,
        ...extendParams
    };
    if (selectedRows.length) {
        apiParams.filters[0].push({ column: 'ID', operator: 'in', value: selectedRows.map((id) => String(id)) });
    }
    const res = await api('post', allProviders ? ORDER_GRID_ALL_PROVIDERS_URL : ORDER_GRID_URL, apiParams);

    const rows = getFormatedData(res.data.data.data);

    if (withExtendColmns) {
        const ids = selectedRows.length ? selectedRows : rows.map((row) => row.ID);
        const productsResponse: OrdersProductsResponseType = await api(
            'get',
            `api/secure/orders/ordersProducts?ids=${ids.join(',')}`
        );
        const productsObject: { [key: string]: OrderProductsType[] } = {};
        productsResponse.data.data.forEach((item) => {
            productsObject[item.ID] = item.ORDER_PRODUCTS;
        });

        const rowsWithProducts: TableRowType[] = [];
        rows.forEach((row) => {
            if (row.ID && productsObject[row.ID]) {
                productsObject[row.ID].map((product) => {
                    rowsWithProducts.push({
                        ...row,
                        PRODUCT_SKU: getOrderProductDisplaySku(product),
                        PRODUCT_PRICE: product.PRODUCT_PRICE,
                        PRODUCT_COUNT: product.COUNT,
                        PRODUCT_CONFIRMED: product.CONFIRMED,
                        PRODUCT_MISSING_COUNT: product.MISSING_COUNT
                    });
                });
            } else {
                rowsWithProducts.push(row);
            }
        });

        return rowsWithProducts;
    }
    return rows;
};

export const getTabs = () => {
    const ableExpeditionPackages = checkPermission('ORDER.EXPEDITION_PACKAGE.LIST');
    const ableMessagesOnBox = checkPermission('MESSAGES_ON_EXPEDITION_BOX.LIST');
    return TABS.filter((item) => {
        if (!ableExpeditionPackages && item.value === 'expedition_packages') {
            return false;
        }
        if (!ableMessagesOnBox && item.value === 'messages_on_expedition_box') {
            return false;
        }
        return true;
    });
};

export const getOrderClaims = async (idOrder: string | number): Promise<ClaimType[]> => {
    const res = await api('post', `api/secure/claims/grid`, {
        filters: [[{ column: 'ORDER_ID', operator: 'eq', value: idOrder }]],
        sorts: [{ column: 'CREATED_AT', direction: 'DESC' }],
        page: 1,
        items: 100,
        columns: ['ID', 'FCN', 'ESHOP_CLAIM_ID', 'ESHOP_ORDER_ID']
    });
    return res.data.data.data;
};

export const getOrderReturns = async (idOrder: string | number) => {
    const res = await api('get', `api/secure/returns/by-order/${idOrder}`);
    return res.data.data;
};
